export type DeviceKey =
  | 'all'
  | 'windows'
  | 'macos'
  | 'linux'
  | 'iphone'
  | 'ipad'
  | 'android'

export const deviceOptions: Array<{ value: DeviceKey; label: string }> = [
  { value: 'all', label: 'All devices' },
  { value: 'iphone', label: 'iPhone' },
  { value: 'ipad', label: 'iPad' },
  { value: 'android', label: 'Android' },
  { value: 'windows', label: 'Windows' },
  { value: 'macos', label: 'macOS' },
  { value: 'linux', label: 'Linux' },
]

export const platformLabels: Record<DeviceKey, string> = {
  all: 'All devices',
  iphone: 'iPhone',
  ipad: 'iPad',
  android: 'Android',
  windows: 'Windows',
  macos: 'macOS',
  linux: 'Linux',
}

export type HelpArticle = {
  slug: string
  title: string
  description: string
  tags: string[]
  platformTags: DeviceKey[]
  category: 'import' | 'privacy' | 'faq' | 'setup'
}

export const helpArticles: HelpArticle[] = [
  {
    slug: 'import-iphone',
    title: 'Import contacts from iPhone',
    description: 'Enable the Contact Picker, choose people, and launch drafts.',
    tags: ['iphone', 'import', 'contacts', 'safari'],
    platformTags: ['iphone'],
    category: 'import',
  },
  {
    slug: 'import-ipad',
    title: 'Import contacts from iPad',
    description: 'Use Safari on iPadOS to import contacts quickly.',
    tags: ['ipad', 'import', 'contacts', 'safari'],
    platformTags: ['ipad'],
    category: 'import',
  },
  {
    slug: 'import-android',
    title: 'Import contacts from Android',
    description: 'Pick contacts, review, and send drafts in your SMS app.',
    tags: ['android', 'import', 'contacts', 'chrome'],
    platformTags: ['android'],
    category: 'import',
  },
  {
    slug: 'windows-phone-link',
    title: 'Send drafts with Windows Phone Link',
    description: 'Pair your phone and Windows PC to send texts from your desktop.',
    tags: ['windows', 'android', 'phone-link', 'setup'],
    platformTags: ['windows', 'android'],
    category: 'setup',
  },
  {
    slug: 'privacy-security',
    title: 'Privacy and security',
    description: 'Understand what stays on your device and what never leaves it.',
    tags: ['privacy', 'security', 'data'],
    platformTags: ['windows', 'macos', 'linux', 'iphone', 'ipad', 'android'],
    category: 'privacy',
  },
  {
    slug: 'faq',
    title: 'FAQ',
    description: 'Common questions about drafts, contacts, and data handling.',
    tags: ['faq', 'support'],
    platformTags: ['windows', 'macos', 'linux', 'iphone', 'ipad', 'android'],
    category: 'faq',
  },
]

export const helpBasePath = '/app/help'

export function getHelpPath(slug: string) {
  return `${helpBasePath}/${slug}`
}

export function detectDeviceFromUserAgent(): DeviceKey | 'all' {
  if (typeof navigator === 'undefined') return 'all'
  const ua = navigator.userAgent
  const platform = typeof navigator.platform === 'string' ? navigator.platform : ''

  if (/iPhone/i.test(ua)) return 'iphone'
  if (/iPad/i.test(ua)) return 'ipad'
  if (/Android/i.test(ua)) return 'android'
  if (/Win/i.test(platform) || /Windows/i.test(ua)) return 'windows'
  if (/Mac/i.test(platform) && !/iPhone|iPad|iPod/i.test(ua)) return 'macos'
  if (/Linux/i.test(platform)) return 'linux'

  return 'all'
}

export function filterArticlesByDevice(
  articles: HelpArticle[],
  device: DeviceKey,
) {
  if (device === 'all') return articles
  return articles.filter((article) => article.platformTags.includes(device))
}

export function getRelatedArticles(currentSlug: string, max = 3) {
  const current = helpArticles.find((article) => article.slug === currentSlug)
  if (!current) return helpArticles.slice(0, max)

  const scored = helpArticles
    .filter((article) => article.slug !== currentSlug)
    .map((article) => {
      const sharedTags = article.tags.filter((tag) =>
        current.tags.includes(tag),
      )
      const categoryBoost = article.category === current.category ? 2 : 0
      return {
        article,
        score: sharedTags.length + categoryBoost,
      }
    })
    .sort((a, b) => b.score - a.score)

  return scored.slice(0, max).map((item) => item.article)
}
